import {
    AUTO_POP_MODAL_SUCCESS,
    AUTO_POP_MODAL_SUCCESS_ACTION,
    HYDRATE_CLIENT_STATE,
    HYDRATE_CLIENT_STATE_ACTION,
    LOGIN_SUCCESS,
    PASSWORD_RESET_SUCCESS,
} from './actions';
import { combineActions, handleActions } from 'redux-actions';
import { differenceInDays, format, parseISO } from 'date-fns';
import { getOpenModal, openSignUpModal } from './modal';
import { createSelector } from 'reselect';
import { GlobalState } from '../rootReducer';
import { modalTypes } from '@/enums';

export const DAYS_TO_WAIT_TO_SHOW_MODAL_AGAIN = 7;

export type State = {
    readonly dateLastShown: string;
    readonly disableModalForTesting: boolean;
    readonly hasLoggedInBefore: boolean;
};

export const DEFAULT_STATE: State = {
    dateLastShown: '',
    disableModalForTesting: false,
    hasLoggedInBefore: false,
};

export const reducer = handleActions(
    {
        [combineActions(LOGIN_SUCCESS, PASSWORD_RESET_SUCCESS)]: (state: State): State => {
            return {
                ...state,
                hasLoggedInBefore: true,
            };
        },
        [AUTO_POP_MODAL_SUCCESS]: (state: State, action: AUTO_POP_MODAL_SUCCESS_ACTION) => {
            return {
                ...state,
                dateLastShown: action.payload,
            };
        },
        [HYDRATE_CLIENT_STATE]: (state: State, action: HYDRATE_CLIENT_STATE_ACTION) => {
            return {
                ...state,
                dateLastShown: action.payload.dateLastShown,
                disableModalForTesting: action.payload.disableModalForTesting,
                hasLoggedInBefore: action.payload.hasLoggedInBefore,
            };
        },
    },
    DEFAULT_STATE
);

/* SELECTORS */
const stateSelector = (state: GlobalState): State => state.autoPopModal;

export const getUiDateLastShown = createSelector(stateSelector, (state) => state.dateLastShown);
export const getUiHasLoggedInBefore = createSelector(stateSelector, (state) => state.hasLoggedInBefore);
export const getUiModalShown = createSelector(getUiDateLastShown, (date) => Boolean(date));
export const shouldStopAutoPopup = createSelector(stateSelector, (state) => state.disableModalForTesting);

export const shouldPopModal = createSelector(
    [shouldStopAutoPopup, getOpenModal, getUiDateLastShown, getUiModalShown, getUiHasLoggedInBefore],
    (abort, openModal, dateLastShown, hasSeenModal, hasLoggedInBefore) => {
        if (abort) {
            return false;
        }

        let notSeenRecently = false;
        if (Boolean(dateLastShown)) {
            const parsedDateLastShown = parseISO(dateLastShown);
            const difference = differenceInDays(new Date(), parsedDateLastShown);
            notSeenRecently = difference >= DAYS_TO_WAIT_TO_SHOW_MODAL_AGAIN;
        }
        const popModal = (!hasSeenModal || notSeenRecently) && !hasLoggedInBefore && openModal === modalTypes.NONE;
        return popModal;
    }
);

/* ACTION CREATORS */
export const autoOpenSignUpModal = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldPopModal(state)) {
        return Promise.all([
            dispatch({
                payload: format(new Date(), 'yyyy-MM-dd'),
                type: AUTO_POP_MODAL_SUCCESS,
            } as AUTO_POP_MODAL_SUCCESS_ACTION),
            dispatch(openSignUpModal()),
        ]);
    } else {
        return Promise.resolve({});
    }
};
